import type {RouteLocationNormalizedLoaded, RouteLocationRaw} from "#vue-router";

// Where the header's back arrow leads: up to the list the user came from (the agenda, My Event, Speakers, ...) in one
// tap, however many detail pages they opened on the way (session > speaker > session > speaker ...). iOS swipe back
// and Android back stay one step at a time.
//
// The arrow goes back through the browser history (router.go), it never pushes: the old arrow pushed the previous page
// as a new entry, so every tap of it made the history longer and swipe back then walked forward again. To know how far
// back the list is, every page is recorded under its history position (vue-router keeps `position` in history.state)
// with its title and whether it is a list. The record is kept in sessionStorage, like the history, so it survives a
// reload.
//
// When the history does not reach a list or the page's natural parent (the page was opened from a notification or a
// link), the arrow replaces the page with its natural parent: a session's is the agenda, a speaker's the speakers ...
export type HistoryPage = {path: string, title: string, list: boolean};
export type UpTarget = {title: string, delta: number} | {title: string, route: RouteLocationRaw};

const STORAGE_KEY = 'wad4-history-pages';

// The natural parent of each detail page, for a page opened without one in the history.
const NATURAL_PARENTS: Record<string, {name: string, title: string, withId?: boolean}> = {
  'events-eventId-session-id': {name: 'events-eventId-agenda', title: 'Agenda'},
  'events-eventId-session-id-questions': {name: 'events-eventId-session-id', title: 'Session', withId: true},
  'events-eventId-speaker-id': {name: 'events-eventId-speakers', title: 'Speakers'},
  'events-eventId-partner-id': {name: 'events-eventId-partners', title: 'Partners'},
  'events-eventId-goodie-id': {name: 'events-eventId-goodies', title: 'Goodies'},
  'events-eventId-activity-id': {name: 'events-eventId-booth-activities', title: 'Booth Activities'},
  'events-eventId-attendees-userId-contact-details': {name: 'events-eventId-attendees', title: 'Attendees'},
  'events-eventId-plan': {name: 'events-eventId-agenda', title: 'Agenda'},
};

// Pages that register no header of their own but are lists the arrow can lead back to.
export const UNREGISTERED_LIST_TITLES: Record<string, string> = {
  'events-eventId': 'Home',
  'index': 'Events',
};

const readStored = (): Record<number, HistoryPage> => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '{}');
  } catch {
    return {};
  }
};

export const useUpNavigation = () => {
  const pages = useState<Record<number, HistoryPage>>(STORAGE_KEY, () => import.meta.client ? readStored() : {});
  const router = useRouter();
  const eventsStore = useEventsStore();

  const currentPosition = (): number | null => {
    const position = import.meta.client ? window.history.state?.position : undefined;
    return typeof position === 'number' ? position : null;
  };

  // The page now on screen, at the history entry the router has just written for it.
  const recordPage = (page: HistoryPage): void => {
    const position = currentPosition();
    if(position === null) {
      return;
    }
    pages.value[position] = page;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pages.value));
    } catch {
      // Private mode or full storage: the arrow then falls back to the natural parent after a reload.
    }
  };

  const naturalParent = (route: RouteLocationNormalizedLoaded, fallback?: RouteLocationRaw): {title: string, route: RouteLocationRaw} => {
    const parent = NATURAL_PARENTS[String(route.name)];
    const eventId = route.params.eventId ?? eventsStore.eventData?.id;
    if(parent && eventId) {
      return {
        title: parent.title,
        route: fallback ?? {name: parent.name, params: parent.withId ? {eventId, id: route.params.id} : {eventId}},
      };
    }
    if(fallback) {
      return {title: 'Back', route: fallback};
    }
    return eventId
      ? {title: 'Home', route: {name: 'events-eventId', params: {eventId}}}
      : {title: 'Events', route: {name: 'index'}};
  };

  // The nearest entry back in the history that is a list or the page's natural parent, else the natural parent itself.
  // The walk stops at the first entry it knows nothing about: what lies before it is not this app's history.
  const upTarget = (route: RouteLocationNormalizedLoaded, fallback?: RouteLocationRaw): UpTarget => {
    const parent = naturalParent(route, fallback);
    const parentPath = router.resolve(parent.route).path;
    const position = currentPosition();
    if(position !== null) {
      for(let entry = position - 1; entry >= 0; entry--) {
        const page = pages.value[entry];
        if(!page) {
          break;
        }
        if(page.list || page.path.split('?')[0] === parentPath) {
          return {title: page.list ? page.title : parent.title, delta: entry - position};
        }
      }
    }
    return parent;
  };

  const goUp = (route: RouteLocationNormalizedLoaded, fallback?: RouteLocationRaw): void => {
    const target = upTarget(route, fallback);
    if('delta' in target) {
      router.go(target.delta);
    } else {
      router.replace(target.route);
    }
  };

  return {recordPage, upTarget, goUp};
};
